import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (data && data.authenticated && data.id) {
        setUser({ id: data.id, email: data.email, name: data.name, role: data.role, country: data.country });
      } else {
        setUser(false);
      }
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const register = async (name, email, password, country) => {
    const payload = { name, email, country };
    if (password) payload.password = password;
    const { data } = await axios.post(
      `${API}/auth/register`,
      payload,
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const requestMagicLink = async (email) => {
    const { data } = await axios.post(
      `${API}/auth/magic-link/request`,
      { email },
      { withCredentials: true }
    );
    return data;
  };

  const verifyMagicLink = async (token) => {
    const { data } = await axios.post(
      `${API}/auth/magic-link/verify`,
      { token },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth, requestMagicLink, verifyMagicLink }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
